import { useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';

import { useIwEditingStore } from '@/entities/interwills';
import { COLORS } from '@/shared/constants/colors';
import { numbers } from '@/shared/utils/numbers';

import { DoughnutChart } from './charts/DoughnutChart';
import { DashboardItem } from './DashboardItem';
import { getDistributionChartData } from './helpers';
import {
  DoughnutChartWrapper,
  Icon,
  Legend,
  StyledSegmented,
  TextContainer,
  Value,
} from './styled';

export type ChartType = 'shares' | 'value';

export const EMPTY_VALUES = {
  labels: ['Rick Martin', 'Lu Martin', 'Ann Martin', 'Mike Martin', 'Maria Martin'],
  data: [49.3, 40.5, 3.4, 3.4, 3.4],
};

export const AssetDitribution = () => {
  const { t } = useTranslation('pages/pageInterwill', { keyPrefix: 'dashboard' });
  const { distribution, beneficiaries, assets } = useIwEditingStore();
  const [chartType, setChartType] = useState<ChartType>('shares');

  const chartData = useMemo(
    () =>
      getDistributionChartData({
        distribution,
        assets,
        beneficiaries,
        chartType,
      }),
    [distribution, assets, beneficiaries, chartType],
  );

  const hasData = chartData.data.length > 0;

  return (
    <DashboardItem hasData={hasData}>
      <DoughnutChartWrapper>
        <DoughnutChart
          title={chartType === 'shares' ? t('distribution.byShares') : t('distribution.byValue')}
          data={hasData ? chartData.data : EMPTY_VALUES.data}
          labels={hasData ? chartData.labels : EMPTY_VALUES.labels}
          colors={hasData ? chartData.colors : COLORS.colorAssetDistributionCharts}
          valueType={chartType === 'shares' ? 'percent' : 'price'}
        />
      </DoughnutChartWrapper>
      <Legend>
        <StyledSegmented
          value={chartType}
          onChange={setChartType}
          size="large"
          options={[
            { label: t('distribution.byShares'), value: 'shares' },
            { label: t('distribution.byValue'), value: 'value' },
          ]}
        />
        {chartData.legendItems.map((item) => (
          <TextContainer key={item.id}>
            <Icon $bgColor={item.color} />
            <p>{item.name}:</p>
            <Value>
              {chartType === 'shares'
                ? numbers.formatPercent({ value: item.value, withSign: true, fractionDigits: 1 })
                : numbers.formatPrice(item.rawValue)}
            </Value>
          </TextContainer>
        ))}
      </Legend>
    </DashboardItem>
  );
};
